/**
 * The VR lectern and the buttons on it, as numbers: where each button goes,
 * how far the whole assembly reaches, and how high the lectern must stand so
 * every button is seen. Pure, so the headset lays its buttons out with the
 * same function framing measures the assembly by, and the API's check and
 * the headset cannot disagree (docs/04, "Lectern panel"; ADR-010).
 *
 * Positions are metres in the lectern's own plane: x to the pupil's right,
 * y up the panel, the lectern's origin at 0.
 */
/**
 * The VR lectern (render/ui.ts builds it from this). It follows the pupil's
 * eyes, 0.95 m below them -- 0.65 m high for an adult's 1.6 m, where it stood
 * before -- so the sight line over its top edge is 13.7° down at any eye height
 * and framing's numbers hold for everyone; but it rises where it must, so its
 * lowest button is never within 10 cm of the floor or more than 40° below the
 * eyes (`lecternHeight`).
 */
export const LECTERN = {
    /** Metres ahead of the pupil, along its arc when it swings. */
    distance: 1.9,
    /** Metres below the eyes. */
    belowEye: 0.95,
    minHeight: 0.45,
    /** Radians about x: tilted up towards the pupil. */
    tilt: -0.5,
    /** The panel, in metres. */
    width: 1.3,
    height: 0.78,
    /** The panel's middle above the lectern's own origin. */
    panelY: 0.1,
    /** Metres the whole assembly keeps from every model, all round. */
    clearance: 0.05,
    /** The lowest button's lowest edge stays this far above the floor. */
    floorGap: 0.1,
    /** ... and at most this many degrees below eye level. */
    maxBelow: 40,
    /** Where along the pupil's line it may stand: its arc, then pulled closer when models leave no room. */
    distances: [1.9, 1.6, 1.35],
};
/** The AR panel: floated in the room, dragged by the pupil (render/ui.ts). */
export const AR_PANEL = { width: 0.6, height: 0.34, panelY: 0.06 };
/** Metres left clear at each end of a row on the VR lectern. */
export const BUTTON_MARGIN = 0.05;
/** From one button's middle to the next in a row, as a share of a button's width. */
const ROW_STEP = 1.08;
/**
 * The buttons' measures. A navigation row (Terug · Nog eens · Verder) is one
 * row, as it has always been. Answers go in a grid of at most two columns,
 * each button half the panel wide and tall enough for two lines, rows tight
 * (on the narrow AR panel, one column of 0.56 m buttons):
 * stacked one under another, four long answers ran into the floor and out of
 * the view (Pieter, 28 Sep 2026).
 */
export const BUTTONS = {
    vr: {
        panel: { width: LECTERN.width, bottom: LECTERN.panelY - LECTERN.height / 2 },
        row: { y: -0.41, height: 0.14, width: 0.42, fit: true, margin: BUTTON_MARGIN },
        answers: {
            top: -0.32,
            height: 0.14,
            columnGap: 0.04,
            rowGap: 0.02,
            margin: BUTTON_MARGIN,
            columns: 2,
        },
    },
    ar: {
        panel: { width: AR_PANEL.width, bottom: AR_PANEL.panelY - AR_PANEL.height / 2 },
        row: { y: -0.17, height: 0.07, width: 0.28, fit: false, margin: 0 },
        // One column on the narrow AR panel: two columns there were 0.27 m wide,
        // too narrow for an 80-letter answer at a readable size.
        answers: {
            top: -0.125,
            height: 0.08,
            columnGap: 0.02,
            rowGap: 0.012,
            margin: 0.02,
            columns: 1,
        },
    },
};
/**
 * How wide the buttons in one row on the lectern are. Three of them -- Terug,
 * ↻ Nog eens, Verder -- at their full 0.42 m ran 1.33 m, wider than the 1.3 m
 * panel above them; they narrow until the row fits with a margin, keeping
 * their height and so their text size (docs/04: big targets, large text).
 */
export function buttonRow(count, preferred, panelWidth, margin = BUTTON_MARGIN) {
    const available = panelWidth - 2 * margin;
    // count buttons and count − 1 gaps of (STEP − 1) of a button each.
    const fits = available / (1 + (count - 1) * ROW_STEP);
    const width = Math.min(preferred, fits);
    return { width, step: width * ROW_STEP };
}
/**
 * Where `count` buttons go: a navigation row, or a grid of answers in reading
 * order (left to right, then down), a last one alone on its row centred.
 * ui.ts puts each button exactly here, and `lecternAssembly` measures the
 * same boxes.
 */
export function layoutButtons(count, layout, mode = 'vr') {
    const measures = BUTTONS[mode];
    if (count <= 0)
        return [];
    if (layout === 'row') {
        const { row } = measures;
        const { width, step } = row.fit
            ? buttonRow(count, row.width, measures.panel.width, row.margin)
            : { width: row.width, step: row.width * ROW_STEP };
        return Array.from({ length: count }, (_, i) => ({
            x: (i - (count - 1) / 2) * step,
            y: row.y,
            width,
            height: row.height,
        }));
    }
    const { answers } = measures;
    const columns = Math.min(answers.columns, count);
    const width = (measures.panel.width - 2 * answers.margin - (columns - 1) * answers.columnGap) / columns;
    return Array.from({ length: count }, (_, i) => {
        const row = Math.floor(i / columns);
        const column = i % columns;
        const inRow = Math.min(columns, count - row * columns);
        return {
            x: (column - (inRow - 1) / 2) * (width + answers.columnGap),
            y: answers.top - answers.height / 2 - row * (answers.height + answers.rowGap),
            width,
            height: answers.height,
        };
    });
}
/**
 * The "Voorlezen" button (ADR-033; docs/04, "Voorlezen"): in the top-right
 * corner of the lectern's panel, inset from its top and right edges, inside
 * the panel's own rectangle -- so the navigation row keeps its three buttons
 * and nothing the lectern is measured by moves. As tall in the view as a
 * navigation button, near enough (0.12 m at 1.9 m is 3.6°, the row's 0.14 m
 * 4.2°); in AR the same corner of the panel read from 1 m.
 */
export const VOICE_BUTTON = {
    vr: { width: 0.36, height: 0.12, inset: 0.03 },
    ar: { width: 0.17, height: 0.06, inset: 0.015 },
};
/** What the panel's title keeps clear of beside the button, metres. */
const VOICE_GAP = { vr: 0.02, ar: 0.01 };
/** Where the "Voorlezen" button goes, in the lectern's (or AR panel's) plane. */
export function voiceButton(mode = 'vr') {
    const { width, height, inset } = VOICE_BUTTON[mode];
    const panel = mode === 'vr' ? LECTERN : AR_PANEL;
    return {
        x: panel.width / 2 - inset - width / 2,
        y: panel.panelY + panel.height / 2 - inset - height / 2,
        width,
        height,
    };
}
/**
 * The panel's top-right corner the button takes, metres from the panel's
 * right and top edges: the title wraps short of it, and the text starts
 * below it (render/panel.ts). Kept on every panel of a step with a voice-over,
 * shown or not, so the teacher's switch never reflows the words.
 */
export function voiceReserve(mode = 'vr') {
    const { width, height, inset } = VOICE_BUTTON[mode];
    return { width: inset + width + VOICE_GAP[mode], height: inset + height + VOICE_GAP[mode] };
}
/**
 * The subtitle plate (ADR-035; voice-guide design, H3): when the voice guides
 * a step, the panel's body is hidden and one or two lines of what is being
 * said show on a plate in the panel's plane and tilt, centred, its bottom
 * 3 cm above the top of the navigation row (`BUTTONS.vr.row`), whose buttons
 * stay where they are. Within what the lectern already measures -- the panel
 * above, the navigation row below -- so `lecternAssembly`, framing,
 * "Tekstpaneel vrij van modellen" and the lectern's height are what they
 * were, and the lectern never moves when a class switches between text and
 * voice. Guide mode hides "Voorlezen".
 *
 * *Changed 5 Oct 2026 (Pieter, on a Quest 3):* its top was 3 cm below the
 * panel's top edge, half a metre above the buttons, so the words stood in
 * the scene while the narrator spoke and the eye jumped between them and the
 * buttons. Lower, they leave the view to what happens and sit by the buttons.
 */
export const SUBTITLE_PLATE = {
    vr: { width: 1.24, height: 0.22, gap: 0.03 },
};
/** Where the subtitle plate goes, in the lectern's plane (VR only: AR keeps its text). */
export function subtitlePlate() {
    const { width, height, gap } = SUBTITLE_PLATE.vr;
    const { row } = BUTTONS.vr;
    return {
        x: 0,
        y: row.y + row.height / 2 + gap + height / 2,
        width,
        height,
    };
}
/** The VR panel together with the given buttons. */
export function assemblyOf(groups) {
    let left = -LECTERN.width / 2;
    let right = LECTERN.width / 2;
    let top = LECTERN.panelY + LECTERN.height / 2;
    let bottom = LECTERN.panelY - LECTERN.height / 2;
    for (const button of groups.flat()) {
        left = Math.min(left, button.x - button.width / 2);
        right = Math.max(right, button.x + button.width / 2);
        top = Math.max(top, button.y + button.height / 2);
        bottom = Math.min(bottom, button.y - button.height / 2);
    }
    return { left, right, top, bottom };
}
/** The most buttons a navigation row holds: Terug, Nog eens, Verder, Klaar. */
const ROW_MOST = 4;
/**
 * Everything a step's lectern shows at some moment: the panel, a navigation
 * row (every step has one, or an outcome's, or a hotspot's), the answers of
 * its quiz or prediction, and "Voorlezen" when it has a voice-over. The
 * lectern stands for all of them at once, so answering never moves it.
 */
export function lecternAssembly(step) {
    const answers = Math.max(step.quiz?.options.length ?? 0, step.predict?.options.length ?? 0);
    return assemblyOf([
        layoutButtons(ROW_MOST, 'row'),
        layoutButtons(answers, 'answers'),
        step.voiceover === undefined ? [] : [voiceButton('vr')],
    ]);
}
/** A step without a question: the panel and a navigation row. */
export const NAVIGATION_ASSEMBLY = lecternAssembly({});
/**
 * How high the lectern stands, `distance` ahead of eyes at `eye` metres above
 * the floor: 0.95 m below the eyes, never below 0.45 m -- and higher when its
 * lowest button would otherwise come within 10 cm of the floor or more than
 * 40° below the eyes. That is a seated child's lectern, and a question's with
 * two rows of answers.
 */
export function lecternHeight(eye, assembly = NAVIGATION_ASSEMBLY, distance = LECTERN.distance) {
    // The lowest edge, tilted back: this much below the origin, and this much
    // nearer the pupil than the lectern's own distance.
    const drop = -assembly.bottom * Math.cos(LECTERN.tilt);
    const nearer = assembly.bottom * Math.sin(LECTERN.tilt);
    const ahead = distance - nearer;
    const floor = LECTERN.floorGap + drop;
    const view = eye + drop - Math.tan((LECTERN.maxBelow * Math.PI) / 180) * ahead;
    return Math.max(eye - LECTERN.belowEye, LECTERN.minHeight, floor, view);
}
//# sourceMappingURL=lectern.js.map